import { css, Theme } from '@emotion/react';
import { type maps } from '@birdi/js-sdk';
import { BirdiTheme, fonts } from '@birdi/theme';
import { ContextMenu, contextStyles } from '@birdi/context-menu/ContextMenu';
import { deleteMap } from '@birdi/js-sdk/src/public/maps';
import { APIResponse } from '@birdi/js-sdk/src/helpers';
import { useState } from 'react';
import MapRename from './MapRenameForm';
import { MapShare } from '@birdi/map-share';
import { useMetaSelector } from '../meta/reducer';
import { MoveToFolderModal } from './move-to-folder-modal';
import { MoveToWorkspaceModal } from './move-to-workspace-modal';
import { MoveContextMenu } from './move-context-menu';
import { DuplicateMapModal } from './duplicate-map-modal';
import { AddToSiteModal } from './add-to-site-modal';
import RenameSVG from '@birdi/icons/rename.svg';
import LinkSVG from '@birdi/icons/link.svg';
import CopySVG from '@birdi/icons/copy.svg';
import TrashSVG from '@birdi/icons/trash-2.svg';
import SitesGlobeSVG from '@birdi/icons/sites-globe.svg';
import { hasSites } from '../map-sites/site-features';

export interface MapTableProps {
  maps: maps.Map[];
}

export const NewMapTable = ({ maps }: MapTableProps) => {
  return (
    <div
      css={(theme: BirdiTheme) => css`
        width: 100%;
        overflow-x: auto;
        padding-bottom: 5em;
        min-height: 30vh;
      `}
    >
      <table
        css={(theme: BirdiTheme) => css`
          width: 100%;
          border-collapse: collapse;
          color: ${theme.midnight};

          th {
            text-align: left;
            padding: 0.75rem 1rem;
            font-weight: normal;
            color: ${theme.midnight};
            background-color: ${theme.mono0};
            border-bottom: 1px solid ${theme.mono30};
          }

          td {
            padding: 0.75rem 1rem;
            border-bottom: 1px solid ${theme.mono20};
            vertical-align: middle;
            color: ${theme.midnight};
          }

          .map-name {
            color: ${theme.midnight};
            text-decoration: underline;
            cursor: pointer;
            font-weight: 700;
          }

          .actions-cell {
            text-align: right;
          }
        `}
      >
        <thead>
          <tr>
            <th>Map name</th>
            <th>Folder</th>
            <th>Location</th>
            <th>Created date</th>
            <th>Created by</th>
            <th>Storage</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {maps.map((map, index) => (
            <MapTableRow key={index} map={map} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

type MapTableRowProps = {
  map: maps.Map;
};

const MapTableRow: React.FC<MapTableRowProps> = ({ map }) => {
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [moveToFolderModalOpen, setMoveToFolderModalOpen] = useState(false);
  const [moveToWorkspaceModalOpen, setMoveToWorkspaceModalOpen] =
    useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [addToSiteModalOpen, setAddToSiteModalOpen] = useState(false);
  const meta = useMetaSelector();
  const canCreateShareLink = meta.features?.includes('share-view-only');
  const canUseSites = hasSites(meta.features);
  const currentWorkspaceId = meta.workspace?.id || meta.organisation.id || null;
  // Duplicating needs edit access on the map (the API enforces map:edit), so
  // view-only roles such as Guest don't get an option that would only 403.
  // Keep in step with the same gate in new-map-list.tsx.
  const canDuplicate = Boolean(meta.permissions?.includes('map:edit'));
  // The share-link API needs map:share:public, the same check as the map
  // header's share button. Keep in step with new-map-list.tsx.
  const canShare = Boolean(meta.permissions?.includes('map:share:public'));

  const handleDelete = () => {
    if (
      window.confirm(
        'Are you sure you want to delete this map? This action cannot be undone. Any data, processing jobs, or reports associated with this map will no longer be available.',
      )
    ) {
      if (map.id) {
        deleteMap({ tag: 'k1665s' }, map.id).then(
          (response: APIResponse<void>) => {
            if (response.status === 200) {
              window.location.reload();
            } else {
              window.alert('Cannot delete map.');
            }
          },
        );
      } else {
        console.log('delete failed');
      }
    }
  };

  const menuItems = [
    {
      label: 'Rename map',
      icon: <RenameSVG />,
      onClick: () => setRenameModalOpen(true),
    },
    ...(canShare
      ? [
          {
            label: 'Share map',
            icon: <LinkSVG />,
            onClick: () => setShareModalOpen(true),
          },
        ]
      : []),
    ...(canUseSites
      ? [
          {
            label: 'Add to site',
            icon: <SitesGlobeSVG />,
            onClick: () => setAddToSiteModalOpen(true),
          },
        ]
      : []),
    ...(canDuplicate
      ? [
          {
            label: 'Duplicate map',
            icon: <CopySVG />,
            onClick: () => setDuplicateModalOpen(true),
          },
        ]
      : []),
    {
      label: 'Delete map',
      icon: <TrashSVG />,
      onClick: handleDelete,
    },
  ];

  const storageData = () => {
    const gb = Number(map.totalGB);
    if (!isNaN(gb)) {
      if (gb < 1) {
        return `${map.fileCount || '-'} files | ${(gb * 1024).toFixed(2)} MB`;
      } else {
        return `${map.fileCount || '-'} files | ${gb.toFixed(2)} GB`;
      }
    } else {
      return `${map.fileCount || '-'} files | - GB`;
    }
  };

  return (
    <tr>
      <td>
        <span
          className="map-name"
          onClick={() => {
            if (
              !renameModalOpen &&
              !shareModalOpen &&
              !moveToFolderModalOpen &&
              !moveToWorkspaceModalOpen &&
              !duplicateModalOpen &&
              !addToSiteModalOpen
            ) {
              window.location.href = `/map/next/${map.id}`;
            }
          }}
        >
          {map.name || 'Untitled Map'}
        </span>
      </td>
      <td>{map.folderId ? map.folderName : '-'}</td>
      <td>{map.location?.name || 'Unknown'}</td>
      <td>
        {new Date(map.createdAt).toLocaleDateString('en-US', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })}
      </td>
      <td>
        {map.creatorFname || '-'} {map.creatorLname || '-'}
      </td>
      <td>{storageData()}</td>
      <td className="actions-cell">
        <div
          css={css`
            position: relative;
          `}
          onClick={(event: any) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <ContextMenu alignRight>
            {() => (
              <div
                css={(theme) => css`
                  ${contextStyles(theme)};
                  min-width: 180px;
                `}
              >
                {menuItems.slice(0, 2).map((item, index) => (
                  <button
                    key={index}
                    type="button"
                    css={(theme: BirdiTheme) => css`
                      display: flex;
                      align-items: center;
                      gap: 0.5rem;
                      width: 100%;
                      padding: 0.5rem 0.75rem;
                      border: none;
                      background: none;
                      cursor: pointer;
                      text-align: left;
                      &:hover {
                        background-color: ${theme.sand};
                      }
                    `}
                    onClick={item.onClick}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                ))}
                <MoveContextMenu
                  onMoveToWorkspace={() => setMoveToWorkspaceModalOpen(true)}
                  onMoveToFolder={() => setMoveToFolderModalOpen(true)}
                />
                {menuItems.slice(2).map((item, index) => (
                  <button
                    key={index + 2}
                    type="button"
                    css={(theme: BirdiTheme) => css`
                      display: flex;
                      align-items: center;
                      gap: 0.5rem;
                      width: 100%;
                      padding: 0.5rem 0.75rem;
                      border: none;
                      background: none;
                      cursor: pointer;
                      text-align: left;
                      &:hover {
                        background-color: ${theme.sand};
                      }
                    `}
                    onClick={item.onClick}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </ContextMenu>
        </div>
      </td>

      <MapRename
        open={renameModalOpen}
        onChange={setRenameModalOpen}
        initialName={map.name}
        mapId={map.id}
      />
      <MapShare
        open={shareModalOpen}
        onChange={setShareModalOpen}
        mapId={map.id}
        disabled={!canCreateShareLink}
      />
      <MoveToFolderModal
        open={moveToFolderModalOpen}
        onChange={setMoveToFolderModalOpen}
        mapId={map.id}
        currentFolderId={map.folderId || null}
      />
      <DuplicateMapModal
        open={duplicateModalOpen}
        onChange={setDuplicateModalOpen}
        mapId={map.id}
        mapName={map.name}
      />
      <AddToSiteModal
        open={addToSiteModalOpen}
        onChange={setAddToSiteModalOpen}
        mapId={map.id}
        mapName={map.name}
      />
      <MoveToWorkspaceModal
        open={moveToWorkspaceModalOpen}
        onChange={setMoveToWorkspaceModalOpen}
        mapId={map.id}
        currentWorkspaceId={currentWorkspaceId}
      />
    </tr>
  );
};
